import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Pencil, Trash2, Plus, User, LogIn, KeyRound, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

const EMPTY = { email: "", name: "", password: "", ruolo_id: "" };

export default function UtentiTab() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [deleteId, setDeleteId] = useState(null);
  const [resetTarget, setResetTarget] = useState(null); // user pending reset
  const [resetResult, setResetResult] = useState(null); // { email, temp_password }
  const [copied, setCopied] = useState(false);
  const { user: currentUser, impersonate } = useAuth();
  const navigate = useNavigate();

  const isAdmin = currentUser?.role === "admin";

  const handleImpersonate = async (u) => {
    if (!isAdmin) return;
    if (currentUser?.impersonated_by) {
      toast.error("Termina prima la sessione corrente di impersonificazione");
      return;
    }
    const ok = await impersonate(u.id);
    if (ok) {
      toast.success(`Sessione attiva come ${u.name || u.email}`);
      navigate("/");
    } else {
      toast.error("Impossibile cambiare utente");
    }
  };

  const handleResetPassword = async () => {
    if (!resetTarget) return;
    try {
      const { data } = await api.post(`/users/${resetTarget.id}/reset-password`);
      setResetResult(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setResetTarget(null);
    }
  };

  const copyTempPassword = async () => {
    if (!resetResult?.temp_password) return;
    try {
      await navigator.clipboard.writeText(resetResult.temp_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copia non riuscita");
    }
  };

  const closeResetDialog = () => {
    setResetTarget(null);
    setResetResult(null);
    setCopied(false);
  };

  const load = async () => {
    try {
      const [u, r] = await Promise.all([api.get("/users"), api.get("/roles")]);
      setUsers(u.data);
      setRoles(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ email: u.email, name: u.name || "", password: "", ruolo_id: u.ruolo_id || "" });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name) return toast.error("Email e nome obbligatori");
    if (!editing && !form.password) return toast.error("Password obbligatoria");
    try {
      const payload = {
        email: form.email,
        name: form.name,
        ruolo_id: form.ruolo_id || null,
      };
      if (form.password) payload.password = form.password;
      if (editing) {
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Utente aggiornato");
      } else {
        await api.post("/users", { ...payload, password: form.password });
        toast.success("Utente creato");
      }
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/users/${deleteId}`);
      toast.success("Utente eliminato");
      setDeleteId(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm" style={{ color: "var(--ds-text-secondary)" }}>
          Gestisci gli utenti che possono accedere al gestionale e assegna loro un ruolo.
        </p>
        <Button onClick={openCreate} className="ds-btn-primary" data-testid="user-add">
          <Plus size={16} className="mr-1.5" /> Nuovo utente
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Utente</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Ruolo</TableHead>
            <TableHead className="text-right">Azioni</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-6 text-sm" style={{ color: "var(--ds-text-secondary)" }}>Nessun utente</TableCell></TableRow>}
          {users.map((u) => (
            <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
              <TableCell className="font-medium">
                <span className="inline-flex items-center gap-2"><User size={14} style={{ color: "var(--ds-primary)" }} /> {u.name || "—"}</span>
              </TableCell>
              <TableCell className="text-sm">{u.email}</TableCell>
              <TableCell className="text-sm">
                {u.role === "admin" ? (
                  <span className="text-xs font-medium px-2 py-1 rounded-full" style={{ background: "rgba(180,107,82,0.15)", color: "var(--ds-primary)" }}>
                    Amministratore
                  </span>
                ) : (
                  u.ruolo_nome || <span style={{ color: "var(--ds-text-secondary)" }}>—</span>
                )}
              </TableCell>
              <TableCell className="text-right">
                {u.role !== "admin" && (
                  <>
                    {isAdmin && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleImpersonate(u)}
                        data-testid={`user-impersonate-${u.id}`}
                        title={`Accedi come ${u.name || u.email}`}
                      >
                        <LogIn size={14} style={{ color: "var(--ds-primary)" }} />
                      </Button>
                    )}
                    {isAdmin && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setResetTarget(u); setResetResult(null); }}
                        data-testid={`user-reset-password-${u.id}`}
                        title={`Reimposta password di ${u.name || u.email}`}
                      >
                        <KeyRound size={14} style={{ color: "#B08A2E" }} />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`user-edit-${u.id}`}><Pencil size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setDeleteId(u.id)} data-testid={`user-delete-${u.id}`}>
                      <Trash2 size={14} style={{ color: "var(--ds-danger)" }} />
                    </Button>
                  </>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-brand text-2xl font-light">{editing ? "Modifica utente" : "Nuovo utente"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 pt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="label-tag">Nome *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-lg" required data-testid="user-form-name" />
              </div>
              <div>
                <Label className="label-tag">Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 rounded-lg" required data-testid="user-form-email" />
              </div>
            </div>
            <div>
              <Label className="label-tag">Password {editing && "(lascia vuoto per non modificare)"}{!editing && " *"}</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5 rounded-lg"
                required={!editing}
                placeholder={editing ? "Nuova password (opzionale)" : "Minimo 6 caratteri"}
                data-testid="user-form-password"
              />
            </div>
            <div>
              <Label className="label-tag">Ruolo</Label>
              <Select value={form.ruolo_id || "none"} onValueChange={(v) => setForm({ ...form, ruolo_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1.5 rounded-lg" data-testid="user-form-ruolo">
                  <SelectValue placeholder="Seleziona…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessun ruolo</SelectItem>
                  {roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button type="submit" className="ds-btn-primary" style={{ background: "var(--ds-primary)" }} data-testid="user-submit">Salva</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare l'utente?</AlertDialogTitle>
            <AlertDialogDescription>L'utente non potrà più accedere al sistema. Operazione irreversibile.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} style={{ background: "var(--ds-danger)" }}>Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset password dialog — two steps: confirm then show temp password */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => !o && closeResetDialog()}>
        <DialogContent data-testid="user-reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-brand text-2xl font-light">
              {resetResult ? "Password reimpostata" : "Reimposta password utente"}
            </DialogTitle>
          </DialogHeader>

          {!resetResult ? (
            <div className="space-y-3 pt-2">
              <p className="text-sm">
                Stai per reimpostare la password di{" "}
                <span className="font-medium">{resetTarget?.name || resetTarget?.email}</span>.
              </p>
              <p className="text-sm" style={{ color: "var(--ds-text-secondary)" }}>
                Verrà generata una <b>password temporanea casuale</b>. L'utente potrà poi cambiarla
                dalla sua pagina Profilo. La password sarà mostrata una sola volta: dovrai
                comunicarla all'utente in modo sicuro.
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={closeResetDialog}>Annulla</Button>
                <Button
                  onClick={handleResetPassword}
                  className="ds-btn-primary"
                  data-testid="user-reset-password-confirm"
                >
                  <KeyRound size={14} className="mr-1.5" /> Genera password
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3 pt-2">
              <p className="text-sm">
                Password temporanea generata per{" "}
                <span className="font-medium">{resetResult.email}</span>:
              </p>
              <div
                className="flex items-center gap-2 rounded-lg p-3"
                style={{ background: "var(--ds-bg)", border: "1px solid var(--ds-border)" }}
              >
                <code
                  className="flex-1 font-mono text-base tracking-wider"
                  data-testid="user-reset-password-value"
                >
                  {resetResult.temp_password}
                </code>
                <Button size="sm" variant="ghost" onClick={copyTempPassword} title="Copia">
                  {copied ? <Check size={16} style={{ color: "#7B8B6F" }} /> : <Copy size={16} />}
                </Button>
              </div>
              <p className="text-xs" style={{ color: "var(--ds-text-secondary)" }}>
                Non sarà più visibile dopo aver chiuso questa finestra. Suggerisci all'utente di
                cambiarla al primo accesso da Profilo → Cambia password.
              </p>
              <DialogFooter>
                <Button onClick={closeResetDialog} className="ds-btn-primary">Ho copiato, chiudi</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
